/**
 * Tic Tac Toe Game Logic
 * Handles game state, move validation, and win detection
 */

/**
 * Create a new empty board
 * @returns {Array} 3x3 array of empty string values
 * Note: Using empty strings instead of null because Firebase doesn't store null arrays well
 */
export const createEmptyBoard = () => {
  return Array(9).fill('');
};

/**
 * Get the symbol for a player (X or O)
 * @param {string} playerId - The player ID
 * @param {string} hostId - The host player ID
 * @returns {string} 'X' or 'O'
 */
export const getPlayerSymbol = (playerId, hostId) => {
  return playerId === hostId ? 'X' : 'O';
};

/**
 * Check if a move is valid
 * @param {Array} board - Current board state
 * @param {number} index - Position to check (0-8)
 * @returns {boolean} True if move is valid
 */
export const isValidMove = (board, index) => {
  return index >= 0 && index < 9 && (board[index] === null || board[index] === '');
};

/**
 * Make a move on the board
 * @param {Array} board - Current board state
 * @param {number} index - Position to place symbol (0-8)
 * @param {string} symbol - Player symbol ('X' or 'O')
 * @returns {Array} New board state
 */
export const makeMove = (board, index, symbol) => {
  if (!isValidMove(board, index)) {
    return board;
  }

  const newBoard = [...board];
  newBoard[index] = symbol;
  return newBoard;
};

/**
 * All possible winning combinations
 */
const WINNING_COMBINATIONS = [
  [0, 1, 2], // Top row
  [3, 4, 5], // Middle row
  [6, 7, 8], // Bottom row
  [0, 3, 6], // Left column
  [1, 4, 7], // Middle column
  [2, 5, 8], // Right column
  [0, 4, 8], // Diagonal top-left to bottom-right
  [2, 4, 6], // Diagonal top-right to bottom-left
];

/**
 * Check if there's a winner
 * @param {Array} board - Current board state
 * @returns {Object|null} Winner info or null
 */
export const checkWinner = (board) => {
  for (const combination of WINNING_COMBINATIONS) {
    const [a, b, c] = combination;

    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return {
        winner: board[a],
        combination: combination,
      };
    }
  }

  return null;
};

/**
 * Check if the board is full (draw)
 * @param {Array} board - Current board state
 * @returns {boolean} True if board is full
 */
export const isBoardFull = (board) => {
  return board.every((cell) => cell !== null && cell !== '');
};

/**
 * Check if the game is over
 * @param {Array} board - Current board state
 * @returns {Object} Game status
 */
export const getGameStatus = (board) => {
  const winner = checkWinner(board);

  if (winner) {
    return {
      isOver: true,
      winner: winner.winner,
      winningCombination: winner.combination,
      isDraw: false,
    };
  }

  if (isBoardFull(board)) {
    return {
      isOver: true,
      winner: null,
      winningCombination: null,
      isDraw: true,
    };
  }

  return {
    isOver: false,
    winner: null,
    winningCombination: null,
    isDraw: false,
  };
};

/**
 * Get the next player's turn
 * @param {string} currentTurn - Current player's symbol
 * @returns {string} Next player's symbol
 */
export const getNextTurn = (currentTurn) => {
  return currentTurn === 'X' ? 'O' : 'X';
};

/**
 * Get the total number of moves made
 * @param {Array} board - Current board state
 * @returns {number} Number of moves
 */
export const getMoveCount = (board) => {
  return board.filter((cell) => cell !== null && cell !== '').length;
};

/**
 * Get position label for display (e.g., "Top Left", "Center", etc.)
 * @param {number} index - Position index (0-8)
 * @returns {string} Position label
 */
export const getPositionLabel = (index) => {
  const labels = [
    'Top Left',
    'Top Center',
    'Top Right',
    'Middle Left',
    'Center',
    'Middle Right',
    'Bottom Left',
    'Bottom Center',
    'Bottom Right',
  ];

  return labels[index] || '';
};

/**
 * Create initial game state
 * @param {string} hostId - Host player ID
 * @param {string} guestId - Guest player ID
 * @returns {Object} Initial game state
 */
export const createInitialGameState = (hostId, guestId) => {
  // Randomly decide who gets X (and goes first)
  const hostGoesFirst = Math.random() < 0.5;

  return {
    board: createEmptyBoard(),
    currentTurn: 'X', // X always goes first, but it's randomly assigned
    hostSymbol: hostGoesFirst ? 'X' : 'O',
    guestSymbol: hostGoesFirst ? 'O' : 'X',
    winner: null,
    isDraw: false,
    isGameOver: false,
    winningCombination: null,
    moveCount: 0,
    startedAt: Date.now(),
  };
};

/**
 * Simple AI move (random valid move)
 * @param {Array} board - Current board state
 * @returns {number|null} Position to move, or null if no moves available
 */
export const getAIMove = (board) => {
  const validMoves = [];

  for (let i = 0; i < board.length; i++) {
    if (board[i] === null || board[i] === '') {
      validMoves.push(i);
    }
  }

  if (validMoves.length === 0) {
    return null;
  }

  // Return random valid move
  return validMoves[Math.floor(Math.random() * validMoves.length)];
};

/**
 * Smarter AI move using minimax (optional for future enhancement)
 * For now, we'll use simple strategies:
 * 1. Try to win
 * 2. Block opponent from winning
 * 3. Take center if available
 * 4. Take corner
 * 5. Take any available spot
 */
export const getSmartAIMove = (board, aiSymbol) => {
  const opponentSymbol = aiSymbol === 'X' ? 'O' : 'X';
  const isEmpty = (cell) => cell === null || cell === '';

  // Try to win
  for (let i = 0; i < board.length; i++) {
    if (isEmpty(board[i])) {
      const testBoard = [...board];
      testBoard[i] = aiSymbol;
      if (checkWinner(testBoard)) {
        return i;
      }
    }
  }

  // Block opponent from winning
  for (let i = 0; i < board.length; i++) {
    if (isEmpty(board[i])) {
      const testBoard = [...board];
      testBoard[i] = opponentSymbol;
      if (checkWinner(testBoard)) {
        return i;
      }
    }
  }

  // Take center if available
  if (isEmpty(board[4])) {
    return 4;
  }

  // Take a corner
  const corners = [0, 2, 6, 8];
  const availableCorners = corners.filter((i) => isEmpty(board[i]));
  if (availableCorners.length > 0) {
    return availableCorners[Math.floor(Math.random() * availableCorners.length)];
  }

  // Take any available spot
  return getAIMove(board);
};

/**
 * Validate game state
 * @param {Object} gameState - Game state to validate
 * @returns {boolean} True if valid
 */
export const isValidGameState = (gameState) => {
  if (!gameState || typeof gameState !== 'object') {
    return false;
  }

  if (!Array.isArray(gameState.board) || gameState.board.length !== 9) {
    return false;
  }

  if (!['X', 'O'].includes(gameState.currentTurn)) {
    return false;
  }

  return true;
};
